import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SafeArea } from "@/components/ios/SafeArea";
import { ScreenHeader } from "@/components/ios/ScreenHeader";
import { Composer } from "@/components/chat/Composer";
import { ChatMessage } from "@/components/ChatMessage";
import { ColonySelector } from "@/components/ColonySelector";
import { BrainSelector } from "@/components/BrainSelector";
import { useColonyContext } from "@/hooks/useColonyContext";
import { useChatWithPersistence } from "@/hooks/useChatWithPersistence";
import { sessionPersistence } from "@/utils/sessionPersistence";
import { Brain as BrainIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ModeSelector } from "@/components/components/ModeSelector";

export function ChatScreen() {
  const navigate = useNavigate();
  const { activeColony, activeColonyId } = useColonyContext();
  const { brainId } = useParams<{ brainId?: string }>();
  
  const cid = activeColonyId;
  const effectiveBrainId = brainId || 'alt-me';
  const brains = useMemo(() => activeColony?.brains || [], [activeColony?.brains]);
  if (!cid) return null;

  const storageKey = `col:${cid}:brain:${effectiveBrainId}:chat:v1`;
  const chat = useChatWithPersistence({ storageKey, keepAlive: true });
  
  const [input, setInput] = useState("");
  const [currentMode, setCurrentMode] = useState<"chat" | "debate" | "autonomous">("chat");
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const localScrollRef = useRef<{ isAutoScrolling: boolean; lastHeight: number }>({ isAutoScrolling: false, lastHeight: 0 });

  const brain = useMemo(() => {
    return brains.find(b => b.id === effectiveBrainId) || (effectiveBrainId === 'alt-me' ? {
      id: 'alt-me', name: 'Alt-me', role: 'AI Orchestrator', color: '#6366f1', status: 'active', colonyId: cid
    } as any : undefined);
  }, [brains, effectiveBrainId, cid]);

  const brainLabel = brain?.name || (effectiveBrainId === 'alt-me' ? 'Alt-me' : effectiveBrainId);

  const send = async () => {
    const text = input.trim();
    if (!text || chat.isSending) return;
    setInput("");

    let targetBrainId = effectiveBrainId;
    const mentionMatch = text.match(/^@(\w+)/);
    if (mentionMatch) {
      const mentionName = mentionMatch[1].toLowerCase();
      const mentionedBrain = brains.find(b => (b.name || '').toLowerCase() === mentionName);
      if (mentionedBrain) {
        targetBrainId = mentionedBrain.id;
      }
    }

    const orchestrate = targetBrainId === 'alt-me';
    const mode = currentMode === 'autonomous' ? 'auto' : currentMode;
    await chat.send(text, {
      endpoint: `/api/colony/${cid}/interact`,
      body: { 
        mode, 
        brainId: targetBrainId, 
        orchestrate, // Alt-me → PromptBuilder (Echo Timeline); others → Roleplay
        messages: [{ role: "user", content: text }] 
      }
    });
  };

  const handleBackToControlRoom = () => {
    navigate("/control-room");
  };

  const handleModeChange = (mode: "chat" | "debate" | "autonomous") => {
    setCurrentMode(mode);
    try {
      localStorage.setItem(`reasoning_mode_${cid}_${effectiveBrainId}`, mode);
    } catch {}
    console.log(`Switched to ${mode} mode`);
  };

  // Track session for navigation continuity
  useEffect(() => {
    if (cid) {
      try { sessionPersistence.setLastActive(cid, `/chat/${cid}/${effectiveBrainId}`); } catch { /* noop */ }
    }
  }, [cid, effectiveBrainId]);

  // Restore saved mode per colony/brain
  useEffect(() => {
    try {
      const savedMode = localStorage.getItem(`reasoning_mode_${cid}_${effectiveBrainId}`);
      if (savedMode && ['chat','debate','autonomous'].includes(savedMode)) {
        setCurrentMode(savedMode as typeof currentMode);
      }
    } catch {}
  }, [cid, effectiveBrainId]);

  const getPlaceholder = () => {
    switch (currentMode) {
      case 'debate':
        return `Debate with ${brainLabel}...`;
      case 'autonomous':
        return `Research with ${brainLabel}...`;
      case 'chat':
      default:
        return `Chat with ${brainLabel}...`;
    }
  };

  // Smooth auto-scroll on new content, with bottom-threshold guard
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 120;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isAtBottom = distanceFromBottom < threshold;
    if (!isAtBottom && !localScrollRef.current.isAutoScrolling) return;
    localScrollRef.current.isAutoScrolling = true;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    const t = setTimeout(() => { localScrollRef.current.isAutoScrolling = false; }, 350);
    return () => clearTimeout(t);
  }, [chat.messages.length, chat.isTyping]);

  return (
    <SafeArea>
      <ScreenHeader 
        title={brainLabel} 
        right={(
          <div className="flex items-center gap-2">
            <ColonySelector variant="minimal" currentColonyId={cid} />
          </div>
        )}
        onBack={handleBackToControlRoom}
      />
      <BrainSelector
        brains={brains}
        activeBrainId={effectiveBrainId}
        colonyId={cid}
        onAddBrain={() => navigate(`/add-brain?colonyId=${cid}`)}
      />
      <div className="px-4 py-2 border-b border-ios-separator bg-background">
        <ModeSelector 
          currentMode={currentMode} 
          onModeChange={handleModeChange}
        />
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 scroll-pb-chat min-h-0">
        {chat.messages.length === 0 && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            Start a conversation with {brainLabel}.
          </div>
        )}
        {chat.messages.map((m) => (
          <ChatMessage key={m.id} message={m} brain={m.sender === 'assistant' ? brain : undefined} />
        ))}
         {chat.isTyping && (
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shrink-0">
              <BrainIcon size={20} className="text-primary-foreground animate-pulse" />
            </div>
            <div className="flex-1">
              <div className="mb-2">
                <span className="ios-body font-medium text-foreground">{brain?.name || 'Assistant'}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  {currentMode === 'chat' && 'Thinking...'}
                  {currentMode === 'debate' && 'Analyzing perspectives...'}
                  {currentMode === 'autonomous' && 'Researching deeply...'}
                </span>
              </div>
              <div className="bg-chat-assistant rounded-2xl px-4 py-3">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              </div>
            </div>
          </div>
         )}
        <div ref={endRef} />
      </div>
      <Composer
        value={input}
        onChange={setInput}
        onSend={send}
        canSend={!!input.trim() && !chat.isSending}
        isSending={chat.isSending}
        placeholder={getPlaceholder()}
        brains={brains as any}
        currentMode={currentMode}
        onMentionBrain={(brainId) => {
          if (brainId && brainId !== effectiveBrainId) {
            navigate(`/chat/${cid}/${brainId}`);
          }
        }}
      />
    </SafeArea>
  );
}